import { useMelioIntl } from '@melio/ar-domain';
import { Button, Group } from '@melio/penny';
import { PayorPaymentRequestDetailsPayeeDetails } from '@melio/platform-api';
import { forwardRef } from '@melio/platform-utils';
import React from 'react';

import { NewGuestPaymentLayout } from '../../../layout';
import { CustomPaymentInstructions, FundingSourceSelection, PaymentRequestDetailsHeader } from '../components';
import { InvoicePaymentRequestDetails } from '../types';

export type GuestPaymentRequestDetailsScreenProps = {
  isLoading: boolean;
  isSubmitting: boolean;
  onAddPlaidAccount: VoidFunction;
  fundingSourceName?: string;
  onDeleteBankAccount: VoidFunction;
  onViewInvoice: VoidFunction;
  payeePaymentRequestDetails: PayorPaymentRequestDetailsPayeeDetails;
  invoicePaymentRequestDetails: InvoicePaymentRequestDetails;
};

export const GuestPaymentRequestDetailsScreen = forwardRef<GuestPaymentRequestDetailsScreenProps>(
  (
    {
      payeePaymentRequestDetails,
      invoicePaymentRequestDetails,
      isLoading,
      isSubmitting,
      onAddPlaidAccount,
      fundingSourceName,
      onDeleteBankAccount,
      onViewInvoice,
    },
    ref
  ) => {
    const { formatMessage } = useMelioIntl();
    const hasAvailablePaymentMethods =
      invoicePaymentRequestDetails.paymentOptions.isCardAllowed ||
      invoicePaymentRequestDetails.paymentOptions.isAchAllowed;

    return (
      <NewGuestPaymentLayout
        isLoading={isLoading}
        data-component={GuestPaymentRequestDetailsScreen.displayName}
        ref={ref}
      >
        <Group variant="vertical" spacing="l" hasDivider>
          <PaymentRequestDetailsHeader
            payeePaymentRequestDetails={payeePaymentRequestDetails}
            invoicePaymentRequestDetails={invoicePaymentRequestDetails}
            onViewInvoice={onViewInvoice}
          />
          {hasAvailablePaymentMethods ? (
            <FundingSourceSelection
              onSubmitByBankAccount={() => {
                onAddPlaidAccount();
              }}
              onSubmitByCard={() => null}
              isSaving={isSubmitting}
              amount={invoicePaymentRequestDetails.totalAmount}
              paymentOptions={invoicePaymentRequestDetails.paymentOptions}
              fundingSourceName={fundingSourceName}
              onDeleteBankAccount={onDeleteBankAccount}
            />
          ) : (
            <Group variant="vertical" spacing="l" ref={ref}>
              {invoicePaymentRequestDetails.paymentOptions.customPayInstructions ? (
                <CustomPaymentInstructions
                  paymentInstructions={invoicePaymentRequestDetails.paymentOptions.customPayInstructions}
                />
              ) : null}
              <Button
                variant="tertiary"
                onClick={onViewInvoice}
                label={formatMessage('ar.newGuestPayment.viewInvoice.button.label')}
              />
            </Group>
          )}
        </Group>
      </NewGuestPaymentLayout>
    );
  }
);

GuestPaymentRequestDetailsScreen.displayName = 'GuestPaymentRequestDetailsScreen';
